/**
 * Sticky "Request Consultation" bar.
 *
 * Desktop: pinned bottom-right button (unobtrusive but always visible)
 * Mobile:  full-width bottom bar (thumb-zone optimized)
 *
 * Auto-hides on:
 *   - admin pages (/admin*)
 *   - the consultation form itself (/request-consultation)
 *   - the thank-you page (/thank-you)
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calendar, X } from "lucide-react";
import { trackEvent } from "../lib/analytics";

const CONSULTATION_URL = "/request-consultation";

const HIDDEN_PATH_PREFIXES = ["/admin", "/request-consultation", "/thank-you"];
const DISMISS_STORAGE_KEY = "vv_sticky_cta_dismissed_v1";
const DISMISS_HOURS = 12; // if dismissed, hide for 12 hours

export function StickyConsultationBar() {
  const loc = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Detect prior dismissal (session-scoped to avoid annoying returning users)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!isNaN(ts) && Date.now() - ts < DISMISS_HOURS * 60 * 60 * 1000) {
          setDismissed(true);
        }
      }
    } catch (_) { /* localStorage may be blocked; ignore */ }
  }, []);

  // Only show after the user has scrolled a bit — feels less pushy on load
  useEffect(() => {
    const hidden = HIDDEN_PATH_PREFIXES.some((p) => loc.pathname.startsWith(p));
    if (hidden) { setVisible(false); return; }
    const onScroll = () => {
      if (window.scrollY > 350 && !dismissed) setVisible(true);
      else if (window.scrollY < 200) setVisible(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loc.pathname, dismissed]);

  if (!visible) return null;

  const handleClick = () => {
    try { trackEvent("consultation_click", { location: "sticky-bar" }); } catch (_) {}
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { window.localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now())); } catch (_) {}
    try { trackEvent("consultation_dismiss", { location: "sticky-bar" }); } catch (_) {}
    setDismissed(true);
    setVisible(false);
  };

  return (
    <>
      {/* Mobile: full-width bar (safe-area padded, above fold on scroll) */}
      <div
        data-testid="sticky-consultation-bar-mobile"
        className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white border-t border-stone-200 shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.15)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      >
        <div className="flex items-center gap-2 p-3">
          <Link
            to={CONSULTATION_URL}
            onClick={handleClick}
            className="flex-1 rounded-full bg-[color:var(--vv-teal)] text-white font-semibold py-3 flex items-center justify-center gap-2"
            data-testid="sticky-cta-mobile-btn"
          >
            <Calendar size={17} />
            Request Consultation
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss consultation banner"
            className="p-2 text-stone-400"
            data-testid="sticky-cta-dismiss-mobile"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Desktop: pinned bottom-right button */}
      <div
        data-testid="sticky-consultation-bar-desktop"
        className="hidden md:flex fixed bottom-6 right-6 z-40 items-center gap-2 rounded-full bg-[color:var(--vv-ink)] text-white shadow-2xl pl-2 pr-1 py-1"
      >
        <Link
          to={CONSULTATION_URL}
          onClick={handleClick}
          className="rounded-full bg-[color:var(--vv-teal)] hover:bg-[color:var(--vv-teal-hover)] transition-colors text-white font-semibold text-sm px-5 py-2.5 flex items-center gap-2"
          data-testid="sticky-cta-desktop-btn"
        >
          <Calendar size={16} />
          Request Consultation
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss consultation banner"
          className="p-1.5 text-white/60 hover:text-white"
          data-testid="sticky-cta-dismiss-desktop"
        >
          <X size={16} />
        </button>
      </div>
    </>
  );
}

export default StickyConsultationBar;
